import {
  LOCATION_PICKER_MAX_QUERY_LENGTH,
  LOCATION_PICKER_REQUEST_TIMEOUT
} from 'src/constants/location_picker'
import { API_PLACE_SUGGESTIONS_PATH } from 'src/constants/urls'

export interface IPlaceSuggestion {
  placeId: string;
  text: string;
  mainText: string;
  secondaryText: string;
}

interface IRawSuggestion {
  placeId?: unknown;
  text?: unknown;
  mainText?: unknown;
  secondaryText?: unknown;
}

export type PlaceSuggestionType = 'address'

export interface IPlaceAutocompleteParams {
  query: string;
  area?: string;
  type?: PlaceSuggestionType;
  signal?: AbortSignal;
}

export type PlaceSearchErrorKind = 'offline' | 'rateLimited' | 'failed'

export class PlaceSearchError extends Error {
  public readonly kind: PlaceSearchErrorKind
  public readonly status?: number

  public constructor (kind: PlaceSearchErrorKind, status?: number) {
    super(`Place search failed: ${kind}`)
    this.kind = kind
    this.status = status
  }
}

const AREA_SLUG = /^[a-z0-9-]+$/

export const characterCount = (text: string): number => Array.from(text.trim()).length

const clip = (text: string): string => Array.from(text.trim()).slice(0, LOCATION_PICKER_MAX_QUERY_LENGTH).join('')

export const buildAutocompleteRequest = ({ query, area, type }: IPlaceAutocompleteParams) => ({
  body: JSON.stringify({
    search_text: clip(query),
    ...(area && AREA_SLUG.test(area) ? { area } : {}),
    ...(type ? { type } : {})
  }),
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  method: 'POST'
})

const asText = (value: unknown): string => typeof value === 'string' ? value : ''

export const parseSuggestions = (json: unknown): IPlaceSuggestion[] => {
  const list = (json as { suggestions?: unknown } | null)?.suggestions
  if (!Array.isArray(list)) {
    throw new PlaceSearchError('failed')
  }

  return (list as IRawSuggestion[])
    .filter((item) => item && typeof item.placeId === 'string' && item.placeId !== '' && asText(item.text) !== '')
    .map((item) => {
      const text = asText(item.text)
      return {
        mainText: asText(item.mainText) || text,
        placeId: item.placeId as string,
        secondaryText: asText(item.secondaryText),
        text
      }
    })
}

export const classifyStatus = (status: number): PlaceSearchErrorKind =>
  status === 429 ? 'rateLimited' : 'failed'

export const fetchPlaceSuggestions = async (params: IPlaceAutocompleteParams): Promise<IPlaceSuggestion[]> => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), LOCATION_PICKER_REQUEST_TIMEOUT)
  const abortWithCaller = () => controller.abort()
  params.signal?.addEventListener('abort', abortWithCaller)

  try {
    const response = await fetch(API_PLACE_SUGGESTIONS_PATH, {
      ...buildAutocompleteRequest(params),
      credentials: 'omit',
      signal: controller.signal
    })

    if (!response.ok) {
      throw new PlaceSearchError(classifyStatus(response.status), response.status)
    }

    return parseSuggestions(await response.json())
  } catch (error) {
    if (params.signal?.aborted) {
      throw error
    }
    if (error instanceof PlaceSearchError) {
      throw error
    }
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false
    throw new PlaceSearchError(offline ? 'offline' : 'failed')
  } finally {
    clearTimeout(timer)
    params.signal?.removeEventListener('abort', abortWithCaller)
  }
}
